import React from 'react'
import { observer } from 'mobx-react'

import Modal from '../../../UniformComponents/Modal'
import BubbleLoader from '../../../UniformComponents/BubbleLoader'
import Typography from '../../../UniformComponents/Typography'
import Button from '../../../UniformComponents/Button'

import './downloadModal.scss'

const PREPARING_STEPS = [
    (slideCount) => `Rendering ${slideCount} slide${slideCount === 1 ? '' : 's'}`,
    () => 'Embedding charts & fonts',
    () => 'Packaging the .pptx file',
]

const PreparingState = observer(({ store }) => {
    const { downloadStepIndex, slideCount } = store

    return (
        <div className="download-modal__body">
            <div className="download-modal__spinner">
                <BubbleLoader />
            </div>

            <Typography type="h3">Preparing your download</Typography>
            <Typography type="p" color="grey">
                This only takes a moment — hang tight.
            </Typography>

            <div className="download-modal__divider" />

            <ul className="download-modal__checklist">
                {PREPARING_STEPS.map((labelFn, idx) => {
                    let modifier = 'download-modal__checklist-row--upcoming'
                    if (idx < downloadStepIndex) modifier = 'download-modal__checklist-row--done'
                    else if (idx === downloadStepIndex) modifier = 'download-modal__checklist-row--active'

                    return (
                        <li key={idx} className={`download-modal__checklist-row ${modifier}`}>
                            <span className="download-modal__checklist-dot" />
                            <span className="download-modal__checklist-label">{labelFn(slideCount)}</span>
                        </li>
                    )
                })}
            </ul>

            <Typography type="tiny" color="grey" cssClass="download-modal__footnote">
                Keep this window open while we build your file.
            </Typography>
        </div>
    )
})

const ReadyState = observer(({ store }) => {
    const { downloadFile, triggerDownload, closeDownloadModal } = store
    if (!downloadFile) return null

    const { filename, size, slideCount } = downloadFile

    return (
        <div className="download-modal__body">
            <div className="download-modal__success">
                <i className="fa-regular fa-circle-check" />
            </div>

            <Typography type="h3">Your presentation is ready</Typography>
            <Typography type="p" color="grey">
                Download it below — it&apos;s also saved to your downloads.
            </Typography>

            <div className="download-modal__file-card">
                <div className="download-modal__file-icon">
                    <i className="fa-regular fa-file-powerpoint" />
                </div>
                <div className="download-modal__file-meta">
                    <Typography type="b">{filename}</Typography>
                    <Typography type="tiny" color="grey">
                        {`PowerPoint · ${slideCount} slide${slideCount === 1 ? '' : 's'} · ${size} MB`}
                    </Typography>
                </div>
            </div>

            <div className="download-modal__actions">
                <Button
                    cssClass="regular tertiary"
                    label="Done"
                    onClick={closeDownloadModal}
                />
                <Button
                    cssClass="regular primary"
                    icon="fa-light fa-arrow-down-to-line"
                    label="Download file"
                    onClick={triggerDownload}
                />
            </div>
        </div>
    )
})

const ErrorState = observer(({ store }) => {
    const { downloadError, startDownload, closeDownloadModal } = store

    return (
        <div className="download-modal__body">
            <div className="download-modal__error">
                <i className="fa-regular fa-circle-exclamation" />
            </div>

            <Typography type="h3">Something went wrong</Typography>
            <Typography type="p" color="grey">
                {downloadError || 'We could not generate your presentation.'}
            </Typography>

            <div className="download-modal__actions">
                <Button
                    cssClass="regular tertiary"
                    label="Close"
                    onClick={closeDownloadModal}
                />
                <Button
                    cssClass="regular primary"
                    label="Try again"
                    onClick={startDownload}
                />
            </div>
        </div>
    )
})

const DownloadPresentationModal = ({ store }) => {
    const { downloadModalState, closeDownloadModal } = store
    if (downloadModalState === 'idle') return null

    const isPreparing = downloadModalState === 'preparing'
    const wrapperClass = `download-modal ${isPreparing ? 'download-modal--no-close' : ''}`

    return (
        <Modal
            show
            cssClass={wrapperClass}
            onClose={isPreparing ? undefined : closeDownloadModal}
        >
            {downloadModalState === 'preparing' && <PreparingState store={store} />}
            {downloadModalState === 'ready' && <ReadyState store={store} />}
            {downloadModalState === 'error' && <ErrorState store={store} />}
        </Modal>
    )
}

export default observer(DownloadPresentationModal)
